import { useAuthToken } from '@/hooks/useAuthToken';
import { countriesToRegion } from '@/lists/countriesToRegion';
import { widgetRegionAtom } from '@heidi/integrations/ehr/data/atoms';
import { toValidRegion } from '@heidi/utils';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useQueryUser } from './query/useQueryUser';

type UserRegionMatchResult =
  | {
      userRegionSameAsWidgetRegion: false;
      isPendingUserRegionSameAsWidgetRegion: true;
    }
  | {
      userRegionSameAsWidgetRegion: boolean;
      isPendingUserRegionSameAsWidgetRegion: false;
    };

export function useIsUserRegionSameAsWidgetRegion(): UserRegionMatchResult {
  const token = useAuthToken();
  const widgetRegion = useAtomValue(widgetRegionAtom);
  const { data: user } = useQueryUser((data) => ({ country: data.country }));

  const userRegionFromCountry = countriesToRegion[user?.country ?? ''];

  // Here we only use the country to determine the region instead of the token
  const effectiveRegion = toValidRegion(userRegionFromCountry);

  return useMemo(() => {
    if (!widgetRegion || !token || !effectiveRegion) {
      return {
        userRegionSameAsWidgetRegion: false,
        isPendingUserRegionSameAsWidgetRegion: true,
      };
    }

    return {
      userRegionSameAsWidgetRegion: !!effectiveRegion && effectiveRegion === widgetRegion,
      isPendingUserRegionSameAsWidgetRegion: false,
    };
  }, [effectiveRegion, token, widgetRegion]);
}
